import { ArrowUpRight, Check, RefreshCw, Trash2, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { IncomingInvoiceUploadModal } from '@/components/invoices/IncomingInvoiceUploadModal'
import { Tooltip } from '@/components/ui/Tooltip'
import type { PayoutStage } from '@/lib/invoice-types'
import { incomingInvoiceUrl } from '@/lib/payout-api'
import { useRemoveIncomingInvoice, useUploadIncomingInvoice } from '@/lib/payout-queries'
import { cn } from '@/lib/utils'

interface Props {
  lineId: string
  stage: PayoutStage
  hasPdf: boolean
  filename: string | null
  incomingInvoiceNumber?: string | null
  share: string
  canManage: boolean
  canView: boolean
  autoSettle?: boolean
  className?: string
}

export function IncomingInvoiceControls({
  lineId,
  stage,
  hasPdf,
  filename,
  incomingInvoiceNumber,
  share,
  canManage,
  canView,
  autoSettle,
  className,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [picked, setPicked] = useState<File | null>(null)
  const [copied, setCopied] = useState(false)
  const upload = useUploadIncomingInvoice()
  const remove = useRemoveIncomingInvoice()

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1500)

    return () => clearTimeout(timer)
  }, [copied])

  if (autoSettle) {
    return <span className={cn('text-sm text-ink-faint', className)}>Via eigen rekening</span>
  }

  const locked = stage === 'BETAALD'
  const busy = upload.isPending || remove.isPending
  // Het factuurnummer is waar je op zoekt en waarmee je afstemt; de bestandsnaam
  // van de ander zegt zelden iets. Alleen als het nummer ontbreekt valt hij terug.
  const label = incomingInvoiceNumber ?? filename ?? 'Inkoopfactuur'
  const fullTitle = [incomingInvoiceNumber, filename].filter(Boolean).join(' · ')
  const extraTitle = fullTitle && fullTitle !== label ? fullTitle : null

  function copy() {
    if (!incomingInvoiceNumber) return
    navigator.clipboard?.writeText(incomingInvoiceNumber)
    setCopied(true)
  }

  function pick() {
    inputRef.current?.click()
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (file) setPicked(file)
  }

  function confirmUpload(fields: {
    incomingInvoiceNumber: string
    incomingInvoiceAmount: string
    incomingInvoiceTotal: string
    incomingInvoiceDifferenceReason?: string
  }) {
    if (!picked) return
    upload.mutate({ lineId, file: picked, ...fields }, { onSuccess: () => setPicked(null) })
  }

  const fileView = hasPdf ? (
    <span className="flex min-w-0 flex-1 items-center gap-1">
      <Tooltip
        tapToToggle={false}
        content={
          copied ? null : extraTitle ? (
            <>
              {extraTitle}
              <br />
              Klik om te kopieren
            </>
          ) : (
            'Klik om te kopieren'
          )
        }
      >
        <button
          type="button"
          onClick={copy}
          aria-label={`Inkoopfactuurnummer ${label} kopieren`}
          className={cn(
            'num min-w-0 truncate rounded-sm text-sm transition-colors duration-150',
            copied ? 'text-accent-700' : 'text-ink-soft hover:text-accent-700',
          )}
        >
          {label}
        </button>
      </Tooltip>
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-status-paid animate-fade-in" />
      ) : canView ? (
        <a
          href={incomingInvoiceUrl(lineId)}
          target="_blank"
          rel="noreferrer"
          aria-label="Inkoopfactuur openen"
          title="Inkoopfactuur openen"
          className="shrink-0 rounded-sm text-ink-faint transition-opacity duration-150 hover:text-accent-700 md:opacity-0 md:focus-visible:opacity-100 md:group-focus-within:opacity-100 md:group-hover:opacity-100"
        >
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      ) : null}
    </span>
  ) : null

  // Betaald of geen beheerrecht: alleen-lezen (evt. het bestand, anders niets).
  if (locked || !canManage) {
    if (!fileView) return <span className={cn('text-sm text-ink-faint', className)}>-</span>
    return <span className={cn('group flex min-w-0 items-center', className)}>{fileView}</span>
  }

  return (
    <span className={cn('group flex min-w-0 items-center gap-1', className)}>
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} />
      {hasPdf ? (
        <>
          {fileView}
          <button
            type="button"
            className="btn-ghost h-7 w-7 shrink-0 p-0 text-ink-faint"
            onClick={pick}
            disabled={busy}
            title="Vervangen"
            aria-label="Inkoopfactuur vervangen"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="btn-ghost h-7 w-7 shrink-0 p-0 text-ink-faint hover:text-status-overdue"
            onClick={() => remove.mutate(lineId)}
            disabled={busy}
            title="Verwijderen"
            aria-label="Inkoopfactuur verwijderen"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </>
      ) : (
        <button type="button" className="btn-ghost h-7 whitespace-nowrap px-2 text-xs" onClick={pick} disabled={busy}>
          <Upload className="h-3.5 w-3.5" />
          Upload factuur
        </button>
      )}
      {picked ? (
        <IncomingInvoiceUploadModal
          file={picked}
          currentNumber={incomingInvoiceNumber}
          share={share}
          replacing={hasPdf}
          pending={upload.isPending}
          onClose={() => setPicked(null)}
          onConfirm={confirmUpload}
        />
      ) : null}
    </span>
  )
}
